import React, { useState } from 'react';
import { Save, Shield, ShieldCheck, ShieldOff, Copy, Check, AlertCircle } from 'lucide-react';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';
import QRCode from 'qrcode';

interface MFAStatus {
  enabled: boolean;
  secret?: string;
  qr_code?: string;
}

// Helper function to generate a base32-encoded TOTP secret
const generateTOTPSecret = (): string => {
  const base32Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  const secretLength = 32; // Standard TOTP secret length
  let secret = '';
  
  // Generate random base32 string
  const randomBytes = new Uint8Array(secretLength);
  crypto.getRandomValues(randomBytes);
  
  for (let i = 0; i < secretLength; i++) {
    secret += base32Chars[randomBytes[i] % base32Chars.length];
  }
  
  return secret;
};

// ============================================
// Password Change Section Component
// ============================================
const PasswordChangeSection: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [success, setSuccess] = useState('');

  // useMutation for password change
  const changePasswordMutation = $api.useMutation('put', '/api/settings/change-password');

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    setSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setValidationError('Tous les champs sont requis');
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 8) {
      setValidationError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    changePasswordMutation.mutate(
      {
        body: {
          old_password: currentPassword,
          new_password: newPassword,
        },
      },
      {
        onSuccess: () => {
          setSuccess('Mot de passe modifié avec succès');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        },
      }
    );
  };

  const displayError = validationError || changePasswordMutation.error;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Changer le mot de passe</h3>
      
      {displayError && <ErrorMessage error={displayError} className="mb-4" />}
      {success && <SuccessMessage message={success} className="mb-4" />}

      <form onSubmit={handlePasswordChange} className="space-y-4">
        <div>
          <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
            Mot de passe actuel
          </label>
          <input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Entrez votre mot de passe actuel"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={changePasswordMutation.isPending}
          />
        </div>

        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
            Nouveau mot de passe
          </label>
          <input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Entrez votre nouveau mot de passe"
            minLength={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={changePasswordMutation.isPending}
          />
          <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères</p>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
            Confirmer le mot de passe
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirmez votre nouveau mot de passe"
            minLength={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={changePasswordMutation.isPending}
          />
        </div>

        <button
          type="submit"
          disabled={changePasswordMutation.isPending}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          <span>{changePasswordMutation.isPending ? 'Modification...' : 'Modifier le mot de passe'}</span>
        </button>
      </form>
    </div>
  );
};

// ============================================
// MFA Section Component
// ============================================
const MFASection: React.FC = () => {
  const [mfaStatus, setMfaStatus] = useState<MFAStatus>({ enabled: false });
  const [success, setSuccess] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [disableMfaCode, setDisableMfaCode] = useState('');
  const [validationError, setValidationError] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // useQuery for MFA status
  const mfaQuery = $api.useQuery('get', '/api/settings/mfa');
  const mfaMutation = $api.useMutation('put', '/api/settings/mfa');

  const handleStartSetup = async () => {
    setValidationError('');
    setSuccess('');

    try {
      // Generate secret on frontend
      const generatedSecret = generateTOTPSecret();
      
      // Get user email from profile query
      const userEmail = mfaQuery.data?.email || 'user@example.com';
      
      // Generate otpauth URL with frontend-generated secret
      const otpauthUrl = `otpauth://totp/Sanctions:${encodeURIComponent(userEmail)}?secret=${generatedSecret}&issuer=Sanctions`;
      
      // Generate QR code as data URL
      const qrCodeDataUrl = await QRCode.toDataURL(otpauthUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setMfaStatus({
        enabled: false,
        secret: generatedSecret,
        qr_code: qrCodeDataUrl,
      });
      setShowSetup(true);
    } catch (err) {
      console.error('MFA setup initiation error:', err);
      setValidationError('Erreur lors de la génération du QR code');
    }
  };

  const handleConfirmSetup = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    setSuccess('');

    if (!mfaCode || mfaCode.length !== 6) {
      setValidationError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    if (!mfaStatus.secret) {
      setValidationError('Secret MFA manquant. Veuillez réessayer.');
      return;
    }

    mfaMutation.mutate(
      {
        body: {
          action: 'activate',
          mfa_code: mfaCode,
          mfa_secret: mfaStatus.secret, // Send the frontend-generated secret
        },
      },
      {
        onSuccess: () => {
          setSuccess('Authentification à deux facteurs activée avec succès');
          setShowSetup(false);
          setMfaCode('');
          mfaQuery.refetch();
        },
      }
    );
  };

  const handleDisable = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    setSuccess('');

    if (!disableMfaCode || disableMfaCode.length !== 6) {
      setValidationError('Veuillez entrer un code à 6 chiffres valide');
      return;
    }

    mfaMutation.mutate(
      {
        body: {
          action: 'deactivate',
          mfa_code: disableMfaCode,
        },
      },
      {
        onSuccess: () => {
          setSuccess('Authentification à deux facteurs désactivée');
          setShowDisableConfirm(false);
          setDisableMfaCode('');
          mfaQuery.refetch();
        },
      }
    );
  };

  const handleCancelSetup = () => {
    setShowSetup(false);
    setMfaCode('');
    setMfaStatus({ enabled: false });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const currentMfaEnabled = mfaQuery.data?.mfa_enabled || mfaStatus.enabled;
  const displayError = validationError || mfaQuery.error || mfaMutation.error;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Shield className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Authentification à deux facteurs (A2F)</h3>
        </div>
        {currentMfaEnabled && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Activée
          </span>
        )}
      </div>

      {displayError && <ErrorMessage error={displayError} className="mb-4" />}
      {success && <SuccessMessage message={success} className="mb-4" />}

      {mfaQuery.isLoading ? (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-500 mt-2">Chargement...</p>
        </div>
      ) : !showSetup ? (
        <div className="space-y-4">
          <p className="text-gray-600">
            {currentMfaEnabled
              ? 'Votre compte est protégé par l\'authentification à deux facteurs. Un code sera requis à chaque connexion.'
              : 'Ajoutez une couche de sécurité supplémentaire en activant l\'authentification à deux facteurs avec une application comme Google Authenticator ou Authy.'}
          </p>

          {!currentMfaEnabled ? (
            <button
              onClick={handleStartSetup}
              disabled={mfaQuery.isLoading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShieldCheck className="h-4 w-4" />
              <span>{mfaQuery.isLoading ? 'Chargement...' : 'Activer l\'A2F'}</span>
            </button>
          ) : (
            <div className="space-y-3">
              {!showDisableConfirm ? (
                <button
                  onClick={() => setShowDisableConfirm(true)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <ShieldOff className="h-4 w-4" />
                  <span>Désactiver l'A2F</span>
                </button>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-2 mb-4">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-900">Confirmer la désactivation</p>
                      <p className="text-sm text-red-700 mt-1">
                        Pour désactiver l'authentification à deux facteurs, veuillez entrer le code actuel de votre application.
                      </p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleDisable} className="space-y-4">
                    <div>
                      <label htmlFor="disable-mfa-code" className="block text-sm font-medium text-red-900 mb-2">
                        Code de vérification actuel
                      </label>
                      <input
                        id="disable-mfa-code"
                        type="text"
                        value={disableMfaCode}
                        onChange={(e) => setDisableMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Code à 6 chiffres"
                        maxLength={6}
                        className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-center text-2xl tracking-widest font-mono"
                        disabled={mfaMutation.isPending}
                        autoComplete="off"
                        autoFocus
                      />
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        disabled={mfaMutation.isPending || disableMfaCode.length !== 6}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {mfaMutation.isPending ? 'Désactivation...' : 'Confirmer la désactivation'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowDisableConfirm(false);
                          setDisableMfaCode('');
                        }}
                        disabled={mfaMutation.isPending}
                        className="bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Configuration de l'A2F</h4>
            <p className="text-sm text-blue-800">
              Scannez le code QR avec votre application d'authentification ou entrez manuellement le secret ci-dessous.
            </p>
          </div>

          {/* QR Code */}
          {mfaStatus.qr_code && (
            <div className="flex flex-col items-center space-y-3">
              <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
                <img
                  src={mfaStatus.qr_code}
                  alt="QR Code MFA"
                  data-testid="mfa-qr-code"
                  className="w-48 h-48"
                />
              </div>
              <p className="text-sm text-gray-600 text-center">
                Scannez ce code avec Google Authenticator, Authy, ou une autre application compatible
              </p>
            </div>
          )}

          {/* Secret Key */}
          {mfaStatus.secret && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ou entrez manuellement ce secret :
              </label>
              <div className="flex items-center space-x-2">
                <code
                  data-testid="mfa-secret"
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm"
                >
                  {mfaStatus.secret}
                </code>
                <button
                  onClick={() => copyToClipboard(mfaStatus.secret!)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copier le secret"
                >
                  {copiedSecret ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}

          {/* Verification Form */}
          <form onSubmit={handleConfirmSetup} className="space-y-4">
            <div>
              <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700 mb-2">
                Entrez le code à 6 chiffres de votre application
              </label>
              <input
                id="mfa-code"
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Code à 6 chiffres"
                maxLength={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest font-mono"
                disabled={mfaMutation.isPending}
                autoComplete="off"
              />
            </div>

            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={mfaMutation.isPending || mfaCode.length !== 6}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mfaMutation.isPending ? 'Vérification...' : 'Confirmer l\'activation'}
              </button>
              <button
                type="button"
                onClick={handleCancelSetup}
                disabled={mfaMutation.isPending}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ============================================
// Main Security Settings Component
// ============================================
const SecuritySettings: React.FC = () => {
  return (
    <div className="space-y-6">
      <PasswordChangeSection />
      <MFASection />
    </div>
  );
};

export default SecuritySettings;
